import './styles.scoped.css'
import {useEffect} from 'react'
import {
    Alert,
    Typography,
    TextField,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    FormControlLabel,
    Checkbox,
    Chip,
    Radio,
    RadioGroup,
} from '@mui/material'
import {Controller} from 'react-hook-form'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {isBefore, startOfDay, startOfToday} from 'date-fns'

// The service care's dates, as the pickers' limits. Either may be missing.
const toDate = value => {
    const date = value ? new Date(value) : null
    return date && !isNaN(date) ? date : undefined
}

const OptionLabel = ({title, hint}) => (
    <span>
        <Typography variant="body1" component="span" display="block">
            {title}
        </Typography>
        <Typography variant="body2" component="span" display="block" color="text.secondary">
            {hint}
        </Typography>
    </span>
)

export default ({form, visitRequirementsData, editing = false}) => {
    const itemStart = toDate(visitRequirementsData?.start_date)
    const itemEnd = toDate(visitRequirementsData?.end_date)
    const occurrenceType = form.watch('occurrence_type')
    const occurrenceEnds = form.watch('occurrence_ends')
    const applyTo = form.watch('apply_to')
    const occurrenceStartDate = form.watch('occurrence_start_date')
    // An edit can reach past visits only once the schedule has started.
    const reachesPast = editing && !!occurrenceStartDate && isBefore(startOfDay(new Date(occurrenceStartDate)), startOfToday())
    useEffect(() => {
        if (!reachesPast && form.getValues('apply_to') === 'all') {
            form.setValue('apply_to', 'upcoming')
        }
    }, [reachesPast])

    const repeatOnMonday = form.watch('repeat_on_monday')
    const repeatOnTuesday = form.watch('repeat_on_tuesday')
    const repeatOnWednesday = form.watch('repeat_on_wednesday')
    const repeatOnThursday = form.watch('repeat_on_thursday')
    const repeatOnFriday = form.watch('repeat_on_friday')
    const repeatOnSaturday = form.watch('repeat_on_saturday')
    const repeatOnSunday = form.watch('repeat_on_sunday')

    const occurrenceTypes = [
        {key: 'one_time_schedule', value: 'One-time schedule'},
        {key: 'daily', value: 'Daily'},
        {key: 'weekly_on_sunday', value: 'Weekly on Sunday'},
        {key: 'every_weekday', value: 'Every Weekday'},
        {key: 'custom', value: 'Custom'},
    ]

    const occurrencePeriods = [
        {key: 'day', value: 'Day'},
        {key: 'week', value: 'Week'},
        {key: 'month', value: 'Month'},
    ]

    // Start on the later of today and the service care's start, so a care that began in the past
    // doesn't backfill visits. A saved start is kept, and the end is always the user's own choice:
    // it is never copied from the service care.
    useEffect(() => {
        // On mobile this step is mounted before the service care is known.
        if (visitRequirementsData && !form.getValues('occurrence_start_date')) {
            const today = startOfToday()
            form.setValue('occurrence_start_date', itemStart && itemStart > today ? itemStart : today)
        }
    }, [visitRequirementsData])

    return (
        <>
            <Typography variant="inputLabel" color="text.secondary">
                Recurrence
            </Typography>
            <FormControl fullWidth error={!!form.formState.errors['occurrence_type']}>
                <Controller
                    {...form.register('occurrence_type')}
                    control={form.control}
                    render={({field}) => (
                        <Select
                            {...field}
                            value={field.value ?? ''}
                            onChange={e => field.onChange(e.target.value)}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 300,
                                    },
                                },
                            }}
                            displayEmpty
                            renderValue={selected => {
                                if (selected === '') {
                                    return <span style={{color: '#aaa'}}>Select ocurrence type</span>
                                }
                                return occurrenceTypes.find(item => item.key === selected)?.value ?? selected
                            }}>
                            {occurrenceTypes.map(item => (
                                <MenuItem key={item?.key} value={item?.key}>
                                    {item?.value}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
                <FormHelperText>{form.formState.errors['occurrence_type']?.message}</FormHelperText>
            </FormControl>
            <br />
            <br />
            {occurrenceType == 'custom' && (
                <>
                    <Typography variant="inputLabel" color="text.secondary">
                        Repeat every
                    </Typography>
                    <div className="form">
                        <TextField
                            placeholder="1"
                            type="number"
                            style={{minWidth: 155}}
                            {...form.register('occurrence_number')}
                            error={!!form.formState.errors['occurrence_number']}
                            helperText={form.formState.errors['occurrence_number']?.message}
                        />
                        <FormControl fullWidth>
                            <Controller
                                {...form.register(`occurrence_period`)}
                                control={form.control}
                                render={({field}) => (
                                    <Select
                                        {...field}
                                        value={field.value ?? ''}
                                        onChange={e => field.onChange(e.target.value)}
                                        MenuProps={{
                                            PaperProps: {
                                                style: {
                                                    maxHeight: 300,
                                                },
                                            },
                                        }}
                                        displayEmpty
                                        renderValue={selected => {
                                            if (selected === '') {
                                                return <span style={{color: '#aaa'}}>Select ocurrence period</span>
                                            }
                                            return occurrencePeriods.find(item => item.key === selected)?.value ?? selected
                                        }}>
                                        {occurrencePeriods.map(item => (
                                            <MenuItem key={item?.key} value={item?.key}>
                                                {item?.value}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                            />
                        </FormControl>
                    </div>
                    <br />
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Repeat On
                    </Typography>
                    <div className="days-form">
                        <Chip
                            label="M"
                            onClick={() => form.setValue('repeat_on_monday', !repeatOnMonday)}
                            sx={theme =>
                                repeatOnMonday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="T"
                            onClick={() => form.setValue('repeat_on_tuesday', !repeatOnTuesday)}
                            sx={theme =>
                                repeatOnTuesday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="W"
                            onClick={() => form.setValue('repeat_on_wednesday', !repeatOnWednesday)}
                            sx={theme =>
                                repeatOnWednesday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="T"
                            onClick={() => form.setValue('repeat_on_thursday', !repeatOnThursday)}
                            sx={theme =>
                                repeatOnThursday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="F"
                            onClick={() => form.setValue('repeat_on_friday', !repeatOnFriday)}
                            sx={theme =>
                                repeatOnFriday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="S"
                            onClick={() => form.setValue('repeat_on_saturday', !repeatOnSaturday)}
                            sx={theme =>
                                repeatOnSaturday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                        <Chip
                            label="S"
                            onClick={() => form.setValue('repeat_on_sunday', !repeatOnSunday)}
                            sx={theme =>
                                repeatOnSunday
                                    ? {bgcolor: theme.palette.primary.main, color: '#ffffff'}
                                    : {bgcolor: theme.palette.primary.hover, color: theme.palette.text.primary}
                            }
                            className="day"
                        />
                    </div>
                    <br />
                </>
            )}
            <div className="form">
                <div className="form-left">
                    <Typography variant="inputLabel" color="text.secondary">
                        Start Time
                    </Typography>
                    <TextField
                        type="time"
                        fullWidth
                        {...form.register('start_time')}
                        error={!!form.formState.errors['start_time']}
                        helperText={form.formState.errors['start_time']?.message}
                    />
                </div>
                <div className="form-right">
                    <Typography variant="inputLabel" color="text.secondary">
                        End Time
                    </Typography>
                    <TextField
                        type="time"
                        fullWidth
                        {...form.register('end_time')}
                        error={!!form.formState.errors['end_time']}
                        helperText={form.formState.errors['end_time']?.message}
                    />
                </div>
            </div>
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Start Date
            </Typography>
            <Controller
                {...form.register('occurrence_start_date')}
                control={form.control}
                render={({field}) => (
                    <DatePicker
                        placeholder="Enter your date"
                        {...field}
                        value={field.value ?? null}
                        onChange={newValue => field.onChange(newValue)}
                        minDate={itemStart}
                        maxDate={itemEnd}
                        enableAccessibleFieldDOMStructure={false}
                        slotProps={{
                            textField: {
                                fullWidth: true,
                                // Undefined leaves the picker's own out-of-range highlighting in charge.
                                error: form.formState.errors['occurrence_start_date'] ? true : undefined,
                                helperText: form.formState.errors['occurrence_start_date']?.message,
                            },
                        }}
                    />
                )}
            />
            <br />
            <br />
            <Typography variant="inputLabel" color="text.secondary">
                Ends
            </Typography>
            <div className="ends-form">
                <Controller
                    {...form.register('occurrence_ends')}
                    control={form.control}
                    render={({field}) => (
                        <RadioGroup {...field} value={field.value ?? ''} onChange={newValue => field.onChange(newValue)} sx={{gap: 2}}>
                            <FormControlLabel value="on" control={<Radio />} label="On" />
                            <FormControlLabel value="after" control={<Radio />} label="After" />
                        </RadioGroup>
                    )}
                />
                <div className="ends-form-right">
                    <Controller
                        {...form.register('occurrence_end_date')}
                        control={form.control}
                        render={({field}) => (
                            <DatePicker
                                placeholder="Enter your date"
                                disabled={occurrenceEnds != 'on'}
                                {...field}
                                value={field.value ?? null}
                                onChange={newValue => field.onChange(newValue)}
                                minDate={itemStart}
                                maxDate={itemEnd}
                                enableAccessibleFieldDOMStructure={false}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: form.formState.errors['occurrence_end_date'] ? true : undefined,
                                        helperText: form.formState.errors['occurrence_end_date']?.message,
                                    },
                                }}
                            />
                        )}
                    />
                    <TextField
                        placeholder="12 occurrences"
                        fullWidth
                        disabled={occurrenceEnds != 'after'}
                        {...form.register('occurrence_total')}
                        error={!!form.formState.errors['occurrence_total']}
                        helperText={form.formState.errors['occurrence_total']?.message}
                    />
                </div>
            </div>
            <FormHelperText error>{form.formState.errors['occurrence_ends']?.message}</FormHelperText>
            {reachesPast && (
                <div className="apply-to">
                    <Typography variant="inputLabel" color="text.secondary">
                        Apply changes to
                    </Typography>
                    <Controller
                        name="apply_to"
                        control={form.control}
                        render={({field}) => (
                            <RadioGroup {...field} value={field.value ?? 'upcoming'} onChange={e => field.onChange(e.target.value)} sx={{gap: 1}}>
                                <FormControlLabel
                                    value="upcoming"
                                    control={<Radio />}
                                    label={<OptionLabel title="Upcoming visits" hint="From today. Past visits stay as they are." />}
                                />
                                <FormControlLabel
                                    value="all"
                                    control={<Radio />}
                                    label={
                                        <OptionLabel
                                            title="All visits not yet worked, including past ones"
                                            hint="From the start date. Worked, in-progress and cancelled visits never change."
                                        />
                                    }
                                />
                            </RadioGroup>
                        )}
                    />
                    {applyTo === 'all' && (
                        <>
                            <Alert severity="warning" className="apply-to-warning">
                                Past visits that haven't been worked will also change to match the schedule: they can be updated, removed or added. A
                                past visit nobody worked counts as missed. Worked, in-progress and cancelled visits stay as they are.
                            </Alert>
                            <Typography variant="inputLabel" color="text.secondary">
                                Reason for changing past visits
                            </Typography>
                            <TextField
                                placeholder="Schedule was set up with the wrong days"
                                fullWidth
                                multiline
                                minRows={2}
                                inputProps={{'aria-label': 'Reason for changing past visits'}}
                                {...form.register('apply_reason')}
                                error={!!form.formState.errors['apply_reason']}
                                helperText={form.formState.errors['apply_reason']?.message}
                            />
                        </>
                    )}
                </div>
            )}
        </>
    )
}
